import { DownloadRounded, IosShare } from '@mui/icons-material';
import { Button, CircularProgress } from '@mui/material';
import { logEvent } from 'firebase/analytics';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { BOARD_LAYOUT } from '../../boardLayout';
import { analyticsInstanceSelector } from '../../services/GameSlice';
import { chipColor, tokens } from '../../theme';

// A short looping replay of a finished game, drawn on a canvas so the very same
// frames can be recorded into a video file to post anywhere.

const STEP_MS = 320;
const DROP_MS = 220;
const HOLD_MS = 2400;
const WIDTH = 520;          // CSS pixels; the canvas is drawn at 2x for sharpness
const PAD = 12;
const GAP = 3;
const STRIP = 34;

const SUITS = { hearts: '♥', diamonds: '♦', clubs: '♣', spades: '♠' };
const RANKS = { ace: 'A', king: 'K', queen: 'Q', jack: 'J' };

const cellW = (WIDTH - PAD * 2 - GAP * 9) / 10;
const cellH = cellW / 1.423;
const HEIGHT = Math.round(PAD * 2 + cellH * 10 + GAP * 9 + STRIP);

const roundRect = (ctx, x, y, w, h, r) => {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
};

const drawChip = (ctx, cx, cy, r, color, ring) => {
    ctx.fillStyle = 'rgba(0,0,0,0.35)';
    ctx.beginPath(); ctx.arc(cx, cy + 1.2, r, 0, Math.PI * 2); ctx.fill();
    // Striped edge, like a poker chip.
    for (let i = 0; i < 16; i++) {
        ctx.fillStyle = i % 2 ? '#F6F1E4' : color;
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.arc(cx, cy, r, (i * Math.PI) / 8, ((i + 1) * Math.PI) / 8);
        ctx.fill();
    }
    ctx.fillStyle = color;
    ctx.beginPath(); ctx.arc(cx, cy, r * 0.72, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = 'rgba(255,255,255,0.85)';
    ctx.lineWidth = Math.max(1, r * 0.12);
    ctx.beginPath(); ctx.arc(cx, cy, r * 0.5, 0, Math.PI * 2); ctx.stroke();
    if (ring) {
        ctx.strokeStyle = tokens.brass;
        ctx.lineWidth = 2.5;
        ctx.beginPath(); ctx.arc(cx, cy, r + 3, 0, Math.PI * 2); ctx.stroke();
    }
};

const pickMimeType = () => {
    if (typeof MediaRecorder === 'undefined') {
        return null;
    }
    return ['video/mp4;codecs=avc1', 'video/mp4', 'video/webm;codecs=vp9', 'video/webm']
        .find(type => MediaRecorder.isTypeSupported(type)) || null;
};

// The card art is served from this site (public/cards) rather than the CDN:
// a canvas that draws another site's images without CORS headers can no longer
// be recorded, and the CDN doesn't send them.
const CARDS = [...new Set(Object.values(BOARD_LAYOUT))];
let cardImages = null;
const loadCardImages = () => {
    if (!cardImages) {
        cardImages = Promise.all(CARDS.map(card => new Promise((resolve) => {
            const image = new Image();
            image.onload = () => resolve([card, image]);
            // A missing card falls back to its drawn label.
            image.onerror = () => resolve([card, null]);
            image.src = `${process.env.PUBLIC_URL}/cards/${card}.png`;
        }))).then(entries => new Map(entries.filter(([, image]) => image)));
    }
    return cardImages;
};

const useCardImages = () => {
    const [images, setImages] = useState(null);
    useEffect(() => {
        let live = true;
        loadCardImages().then(loaded => live && setImages(loaded));
        return () => { live = false; };
    }, []);
    return images;
};

const ReplayClip = ({ history, finalMoves, layout, gameId, onOpen }) => {
    const canvasRef = useRef(null);
    const startRef = useRef(0);
    const [video, setVideo] = useState(null);
    const images = useCardImages();
    const analytics = useSelector(analyticsInstanceSelector);

    const steps = history?.length ? history : null;
    const total = steps ? steps.length : 0;
    const loopMs = total * STEP_MS + HOLD_MS;
    const mimeType = pickMimeType();

    const draw = useCallback((elapsed) => {
        const canvas = canvasRef.current;
        if (!canvas) {
            return;
        }
        const ctx = canvas.getContext('2d');
        const scale = canvas.width / WIDTH;
        ctx.setTransform(scale, 0, 0, scale, 0, 0);

        // Table and mat.
        const lamp = ctx.createRadialGradient(WIDTH * 0.4, HEIGHT * 0.35, 20, WIDTH * 0.4, HEIGHT * 0.35, WIDTH * 0.8);
        lamp.addColorStop(0, '#1A5140');
        lamp.addColorStop(1, tokens.feltDeep);
        ctx.fillStyle = lamp;
        ctx.fillRect(0, 0, WIDTH, HEIGHT);

        const step = total === 0 ? 0 : Math.min(total, Math.floor(elapsed / STEP_MS) + 1);
        const done = total === 0 || elapsed >= total * STEP_MS;
        const intoStep = elapsed - (step - 1) * STEP_MS;

        const board = new Map();
        if (done) {
            (finalMoves || []).forEach(m => board.set(m.pos, m));
        } else {
            steps.slice(0, step).forEach(entry => {
                if (entry.kind === 'remove') {
                    board.delete(entry.pos);
                } else {
                    board.set(entry.pos, entry);
                }
            });
        }
        const latest = !done && steps ? steps[step - 1] : null;

        for (let row = 0; row < 10; row++) {
            for (let col = 0; col < 10; col++) {
                const pos = `${row},${col}`;
                const x = PAD + col * (cellW + GAP);
                const y = PAD + row * (cellH + GAP);
                ctx.fillStyle = '#FBF8F1';
                roundRect(ctx, x, y, cellW, cellH, 3);
                ctx.fill();

                const card = (layout || BOARD_LAYOUT)[pos];
                const art = images?.get(card);
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                if (art) {
                    ctx.save();
                    roundRect(ctx, x, y, cellW, cellH, 3);
                    ctx.clip();
                    ctx.drawImage(art, x, y, cellW, cellH);
                    ctx.restore();
                }
                if (card === 'blank_card') {
                    ctx.fillStyle = '#C9A04A';
                    ctx.font = `${cellH * 0.62}px Figtree, sans-serif`;
                    ctx.fillText('★', x + cellW / 2, y + cellH / 2 + 1);
                } else if (!art) {
                    const [suit, rank] = card.split('_');
                    ctx.fillStyle = suit === 'hearts' || suit === 'diamonds' ? '#C8322B' : '#1B1F1D';
                    ctx.font = `700 ${cellH * 0.42}px Figtree, sans-serif`;
                    ctx.fillText(`${RANKS[rank] || rank}${SUITS[suit]}`, x + cellW / 2, y + cellH / 2 + 1);
                }

                const chip = board.get(pos);
                const cx = x + cellW / 2;
                const cy = y + cellH / 2;
                const r = cellH * 0.36;
                if (chip) {
                    // The newest chip drops in slightly large and settles.
                    const settling = latest && latest.pos === pos && latest.kind !== 'remove' && intoStep < DROP_MS;
                    const grow = settling ? 1 + 0.6 * (1 - intoStep / DROP_MS) : 1;
                    drawChip(ctx, cx, cy, r * grow, chipColor(chip.team) || '#8A958F', done && chip.completedSequence);
                } else if (latest && latest.pos === pos && latest.kind === 'remove') {
                    ctx.setLineDash([3, 3]);
                    ctx.strokeStyle = chipColor(latest.team);
                    ctx.lineWidth = 2;
                    ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.stroke();
                    ctx.setLineDash([]);
                }
            }
        }

        // Brand strip, so a clip posted on its own still says where it's from.
        const y0 = HEIGHT - STRIP + 4;
        [tokens.chips.blue, tokens.chips.green, tokens.chips.red].forEach((color, i) => {
            ctx.fillStyle = color;
            ctx.beginPath(); ctx.arc(PAD + 7 + i * 7, y0 + 11, 6.5, 0, Math.PI * 2); ctx.fill();
        });
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = tokens.parchment;
        ctx.font = '700 14px "Bricolage Grotesque", Figtree, sans-serif';
        ctx.fillText('Play Sequence', PAD + 30, y0 + 11);
        ctx.textAlign = 'right';
        ctx.fillStyle = tokens.parchmentMuted;
        ctx.font = '600 12px Figtree, sans-serif';
        ctx.fillText(done ? 'play-sequence.com' : `Move ${step} of ${total}`, WIDTH - PAD, y0 + 11);
    }, [finalMoves, steps, total, images, layout]);

    const fileName = `play-sequence-${gameId}.${mimeType?.startsWith('video/mp4') ? 'mp4' : 'webm'}`;
    // Wait for the card art so the video never opens on a half-drawn board.
    const canRecord = Boolean(mimeType) && total > 0 && images !== null;
    const canShareFile = typeof navigator !== 'undefined' && typeof navigator.canShare === 'function'
        && typeof File !== 'undefined' && navigator.canShare({ files: [new File([''], fileName, { type: (mimeType || '').split(';')[0] })] });

    // The loop re-reads the latest drawing code without restarting, so a game
    // update arriving mid-way doesn't throw away a half-made video.
    const drawRef = useRef(draw);
    drawRef.current = draw;

    // One clock drives the preview and the video. The first time through is
    // recorded straight away, so the file is ready by the time anyone presses
    // the button — browsers only open the share sheet right after a click, not
    // ten seconds later once a recording has finished.
    useEffect(() => {
        let frame;
        let cancelled = false;
        let recorder = null;
        const chunks = [];

        if (canRecord && canvasRef.current) {
            try {
                recorder = new MediaRecorder(canvasRef.current.captureStream(30), { mimeType, videoBitsPerSecond: 4000000 });
                recorder.ondataavailable = (event) => event.data.size > 0 && chunks.push(event.data);
                recorder.onstop = () => {
                    if (!cancelled) {
                        const type = mimeType.split(';')[0];
                        setVideo(new File([new Blob(chunks, { type })], fileName, { type }));
                    }
                };
            } catch (error) {
                recorder = null;
            }
        }

        startRef.current = performance.now();
        drawRef.current(0);
        recorder?.start();

        const tick = (now) => {
            let elapsed = now - startRef.current;
            if (elapsed >= loopMs) {
                if (recorder?.state === 'recording') {
                    recorder.stop();
                }
                startRef.current = now;
                elapsed = 0;
            }
            drawRef.current(elapsed);
            frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
        return () => {
            cancelled = true;
            cancelAnimationFrame(frame);
            if (recorder?.state === 'recording') {
                recorder.stop();
            }
        };
    }, [loopMs, canRecord, mimeType, fileName]);

    const track = (method) => {
        if (analytics) {
            logEvent(analytics, 'share', { method, content_type: 'result', item_id: gameId });
        }
    };

    const download = (file) => {
        const url = URL.createObjectURL(file);
        const link = document.createElement('a');
        link.href = url;
        link.download = file.name;
        link.click();
        setTimeout(() => URL.revokeObjectURL(url), 5000);
        track('video_download');
    };

    // Runs inside the click, so the share sheet is allowed to open. If sharing
    // is refused for any reason other than the player closing it, save instead.
    const shareVideo = () => {
        if (!video) {
            return;
        }
        if (!canShareFile) {
            download(video);
            return;
        }
        navigator.share({ files: [video], title: 'Play Sequence' })
            .then(() => track('video_share'))
            .catch((error) => {
                if (error?.name !== 'AbortError') {
                    download(video);
                }
            });
    };

    return (
        <div className="replay-clip">
            <button type="button" className="replay-clip-screen" onClick={onOpen} aria-label="Watch the full replay">
                <canvas ref={canvasRef} width={WIDTH * 2} height={HEIGHT * 2} style={{ aspectRatio: `${WIDTH} / ${HEIGHT}` }} />
            </button>
            {canRecord &&
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={video ? (canShareFile ? <IosShare /> : <DownloadRounded />) : <CircularProgress size={14} thickness={5} />}
                    disabled={!video}
                    onClick={shareVideo}
                    sx={{ color: 'text.primary', borderColor: 'rgba(22, 32, 28, 0.25)', mt: 1 }}
                >
                    {!video ? 'Preparing video…' : canShareFile ? 'Share video' : 'Save video'}
                </Button>}
        </div>
    );
};

export default ReplayClip;
